import { catalogOpsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  JourneyProductPage,
  JourneyProductsQuery,
  JourneyRun,
  JourneyStep,
  PostStepAcceptanceRequest,
  PostStepAcceptanceResponse,
  PostStepAcceptanceUndoRequest,
  PostStepRetryItem,
  PostStepRetryRequest,
  PostStepRetryResponse,
  PostStepRetryRun,
  PostSteps,
} from '../types/post-step-retry.api.types';

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } }).response?.status === 404;
}

/**
 * GET jobs/{id} — only the `postSteps` block. Null = nothing to show (an older backend with no
 * block, or a 404): the UI renders today's Enrichment view with no stepper.
 */
export async function getPostSteps(jobId: string): Promise<PostSteps | null> {
  try {
    const detail = await catalogOpsClient
      .get(endpoints.catalogOperations.batchPostSteps(jobId))
      .json<{ postSteps?: PostSteps | null }>();
    return detail.postSteps ?? null;
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

/**
 * GET jobs/{id}/journey/runs — the runs that touched one step, oldest first. 404 = a backend
 * without the journey: null, and the UI falls back to the Enrichment view.
 */
export async function getJourneyRuns(
  jobId: string,
  step: JourneyStep,
): Promise<JourneyRun[] | null> {
  try {
    return await catalogOpsClient
      .get(endpoints.catalogOperations.journeyRuns(jobId), { searchParams: { step } })
      .json<JourneyRun[]>();
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

/** GET jobs/{id}/journey/runs/{runId}/products — one run's products in a step; empty query values dropped. */
export async function getJourneyRunProducts(
  jobId: string,
  runId: string,
  step: JourneyStep,
  query: JourneyProductsQuery,
): Promise<JourneyProductPage> {
  const searchParams: Record<string, string | number> = { step };
  if (query.class) searchParams.class = query.class;
  if (query.q) searchParams.q = query.q;
  if (query.page !== undefined) searchParams.page = query.page;
  if (query.size !== undefined) searchParams.size = query.size;
  return catalogOpsClient
    .get(endpoints.catalogOperations.journeyRunProducts(jobId, runId), { searchParams })
    .json<JourneyProductPage>();
}

/** GET jobs/{id}/products/{productId}/post-step-attempts — oldest first. */
export async function getPostStepAttempts(
  jobId: string,
  productId: string,
): Promise<PostStepRetryItem[]> {
  return catalogOpsClient
    .get(endpoints.catalogOperations.postStepAttempts(jobId, productId))
    .json<PostStepRetryItem[]>();
}

/** POST jobs/{id}/post-step-retries — 200 for a dry run, 202 when started. 409 carries the reason. */
export async function startPostStepRetry(
  jobId: string,
  body: PostStepRetryRequest,
): Promise<PostStepRetryResponse> {
  return catalogOpsClient
    .post(endpoints.catalogOperations.postStepRetries(jobId), { json: body })
    .json<PostStepRetryResponse>();
}

/** POST jobs/{id}/post-step-retries/{retryId}/redeliver — 202 with the updated run. */
export async function redeliverPostStepRetry(
  jobId: string,
  retryId: string,
): Promise<PostStepRetryRun> {
  return catalogOpsClient
    .post(endpoints.catalogOperations.postStepRetryRedeliver(jobId, retryId))
    .json<PostStepRetryRun>();
}

/** POST jobs/{id}/post-step-acceptances — accept unhealthy products as they are; retries skip them. */
export async function acceptPostStepProducts(
  jobId: string,
  body: PostStepAcceptanceRequest,
): Promise<PostStepAcceptanceResponse> {
  return catalogOpsClient
    .post(endpoints.catalogOperations.postStepAcceptances(jobId), { json: body })
    .json<PostStepAcceptanceResponse>();
}

/** POST jobs/{id}/post-step-acceptances/undo — retries pick these products up again. */
export async function undoPostStepAcceptance(
  jobId: string,
  body: PostStepAcceptanceUndoRequest,
): Promise<PostStepAcceptanceResponse> {
  return catalogOpsClient
    .post(endpoints.catalogOperations.postStepAcceptancesUndo(jobId), { json: body })
    .json<PostStepAcceptanceResponse>();
}
